import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Note, NoteUpdate } from '@app/models/note.model';
import { LibService } from '@app/services/lib.service';
import { NoteTitleInputParams } from '@care-connect-japan/ckweblib/lib/data/schema/params/library-params';
import { Subject, filter, takeUntil } from 'rxjs';
import { DateService } from '@app/services/date.service';
import { RecordService } from '@app/services/record.service';
import { Profile } from '@app/models/profile.model';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { Store } from '@ngxs/store';
import { User } from '@app/models/user.model';
import * as moment from 'moment';
import { LIMIT_DATE } from '@app/shared/enum/date';
import { DataSelectors } from '@app/store/data/data.selectors';
import { SelectUser } from '@app/store/data/data.classes';
import { ItemOption, addMonthDuration, durationOptions } from '../facesheet-common';
import { LibNoteService } from '@care-connect-japan/ckweblib';
import { ScheduleService } from '@app/services/schedule.service';
import { AccesslogService } from '@app/services/accesslog.service';
import { ApiService } from '@app/services/api.service';
import { MessageService } from '@app/services/message.service';
import { DialogService } from '@app/services/dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ComponentCanDeactivate } from '@app/can-deactivate.guard';
import { WebviewService } from '@app/services/webview.service';
import { RemoveVisibleOnlyData, SetVisibleOnlyData, TriggerHistory } from '@app/store/schedule/schedule.classes';
import { NoteBlobService } from '@app/services/noteblob.service';
@Component({
  selector: 'app-facesheet-edit',
  templateUrl: './facesheet-edit.component.html',
  styleUrls: ['./facesheet-edit.component.scss']
})
export class FacesheetEditComponent implements OnInit, ComponentCanDeactivate, OnDestroy {
  collapsed: boolean = true;
  record!: Note;
  noteTitleParams!: NoteTitleInputParams;
  profile!: Profile | null;
  dai!: User | null;
  currentUnit!: string;
  isDecide: boolean = true;
  unitOptions: ItemOption[] = [];
  durationOptions: ItemOption[] = durationOptions;
  dateFormat = 'YYYY-MM-DDTHH:mm:ss';
  ownStfOptions: ItemOption[] = [];
  dateDuration: number = 0;
  canLeave: boolean = false;
  protected errorUpdateMsg: string = `編集に失敗しました。\n再度実行してください。`
  protected origin = 'facesheetEdit';
  currentDuration: string | undefined = '';
  faceSheetCacheName: string = 'faceSheetCacheAtPage';
  @ViewChild('titleform') titleformContainer: ElementRef | null = null;
  @ViewChild('notetitle') notetitleEle: ElementRef<any> | null | any = null;
  //
  formId: string = 'facesheet-edit';
  //
  private _form!: FormGroup;
  get form(): FormGroup {
    return this._form;
  }
  set form(form: FormGroup) {
    this._form = form;
  }
  get summary() {
    return this.form.get('summary') as FormControl;
  }
  get handover() {
    return this.form.get('handover') as FormControl;
  }
  get startDate() {
    return this.form.get('startDate') as FormGroup;
  }
  get endDate() {
    return this.form.get('endDate') as FormGroup;
  }

  titleHeight() {
    let height = 0;
    if (!this.titleformContainer) { height = 0 }
    else {
      let findChild = this.titleformContainer.nativeElement.querySelector('.form-body-container');
      if (findChild) { height = findChild?.getBoundingClientRect().height || 0 }
    }
    return { height: height + 'px' };
  }

  private onDestory$: Subject<void>;
  constructor(
    private dateService: DateService,
    private libService: LibService,
    private router: Router,
    private route: ActivatedRoute,
    public recordService: RecordService,
    private store: Store,
    private libNoteService: LibNoteService,
    public scheduleService: ScheduleService,
    private accesslog: AccesslogService,
    private apiService: ApiService,
    protected dialogService: DialogService,
    private snackBar: MatSnackBar,
    private message: MessageService,
    private cd: ChangeDetectorRef,
    private webviewService: WebviewService,
    private noteblobService: NoteBlobService,
  ) {
    this.onDestory$ = new Subject();
  }
  ngOnDestroy(): void {
    this.webviewService.kidsWarningBeforeChangePageIndicator(false);
    this.store.dispatch(new RemoveVisibleOnlyData());
  }

  get isValid() {
    const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    const staffList = this.store.selectSnapshot(DataSelectors.staffList(objectcd));
    const getStaffName = this.scheduleService.getStaffName(staffList);
    const formValue = this.scheduleService.getFormValue(this.form.value, getStaffName(this.record));
    let sdate = moment(formValue.sdate).toDate();
    let edate = moment(formValue.edate).toDate();
    return sdate <= edate;
  }

  async ngOnInit(): Promise<void> {
    this.store.dispatch(new SetVisibleOnlyData(this.formId));
    this.profile = this.store.selectSnapshot(AuthSelectors.profile);
    this.ownStfOptions = [{ label: this.profile?.fullName || '', value: this.profile?.staffCode || '' }];
    this.dai = this.store.selectSnapshot(DataSelectors.selectedUser);
    this.route.data.subscribe(async (data: any) => {
      let { note, noteid } = data['record'];
      this.record = { ...note };
      let createMode = this.getCreateMode(noteid, note.noteid);
      if (!noteid) {
        this.record.noteid = noteid;
      }
      this.isDecide = note.isDecide == null ? !(moment(LIMIT_DATE.EDATE).startOf('day') <= moment(note?.edate).startOf('day')) : note.isDecide;
      if (!this.isDecide) {
        this.record.edate = this.record.sdate;
      }
      this.form = this.recordService.setRecordForm(this.profile, this.record);
      this.noteTitleParams = await this.libService.getFaceSheetParams({ ...note, origin: this.origin }, createMode);
      this.dateDuration = moment(this.record?.edate).diff(moment(this.record?.sdate), 'month');
      this.record.edate = this.record.sdate;
      this.unitOptionsCreate();
      this.cd.detectChanges();
    });
    this.webviewService.kidsWarningBeforeChangePageIndicator(true);
  }

  getCreateMode(currentId: any, checkId: any) {
    // no id && no copy id --> new
    if (!currentId && !checkId) {
      this.errorUpdateMsg = `新規作成に失敗しました。\n再度実行してください。`;
      return { create: true };
    }
    // have current id or any --> edit
    if (!currentId) {
      this.errorUpdateMsg = `複写に失敗しました。\n再度実行してください。`;
    }
    return {};

  }

  unitOptionsCreate() {
    let unit = this.store.selectSnapshot(AuthSelectors.selectedUnit);
    if (!unit) return;

    // unitcd
    let unitParam = {
      label: unit.name,
      value: unit.organizationCode + unit.companyCode + unit.blockCode + '00' + unit.code,
      tag: 'unitcd'
    }
    // offiecd
    let companyParam = {
      label: unit.companyName,
      value: unit.organizationCode + unit.companyCode + '0000000',
      tag: 'officecd'
    }
    // blockcd
    let blockParam = {
      label: unit.blockName,
      value: unit.organizationCode + unit.companyCode + unit.blockCode + '00000',
      tag: 'blockcd'
    }
    this.currentUnit = this.record.ownunitcd;
    this.unitOptions = [companyParam, blockParam, unitParam];
  }

  onChange(): void {
    // this.formChange.emit(this.form);
    this.currentDuration = '';
  }

  canDeactivate() {
    return this.canLeave;
  }

  /**
   * change edate of noteData
   */
  changeDecide(event: any) {
    this.isDecide = !event.checked;
    this.startDateChange();
    if (!this.isDecide) {
      this.currentDuration = '';
    } else {
      let date = addMonthDuration(new Date(), 0);
      let endDate = moment(date);
      this.endDate.patchValue({ date: endDate.format('YYYY-MM-DDTHH:mm:ss'), time: endDate.format('HH:mm') });
    }
    this.cd.detectChanges();
  }

  changeEndDate(value: string | number | any) {
    if (!value) {
      return;
    }
    this.currentDuration = value;
    this.changeDecide({ checked: false });
    if (!this.isDecide) return;
    const startDate = this.dateService.parseFormToDate(this.startDate);
    let duration = parseInt(value) || 0;
    if (duration >= 0 && startDate) {
      let date = addMonthDuration(startDate, parseInt(value));
      let endDate = moment(date).add(-1, 'second');
      this.endDate.patchValue({ date: endDate.format('YYYY-MM-DDTHH:mm:ss'), time: endDate.format('HH:mm') });
    }
  }

  compareDate(): void {
    const startDate = this.dateService.parseFormToDate(this.startDate);
    const endDate = this.dateService.parseFormToDate(this.endDate);

    if (!startDate || !endDate) return;

    if (startDate.getTime() > endDate.getTime()) {
      this.endDate.setErrors({ compareDate: true });
    }
  }

  changeCurrentDate(startOfDate: boolean = false) {
    const date = moment().format(this.dateFormat);
    const timeFormat = 'HH:mm';
    const time = startOfDate ? moment().startOf('date').format(timeFormat) : moment().format(timeFormat);
    this.startDate.patchValue({ date, time });
  }

  changeUnit(unit: string | number) {
    this.currentUnit = unit.toString();
  }

  startDateChange() {
    if (this.isDecide) return;
    let date = addMonthDuration(new Date(), 0);
    let endDate = moment(date);
    this.endDate.patchValue({ date: endDate.format('YYYY-MM-DDTHH:mm:ss'), time: endDate.format('HH:mm') });
  }

  cancel() {
    if (this.dai) {
      this.store.dispatch(new SelectUser(this.dai));
    }
    this.close();
  }

  async submit() {
    const isReady = this.notetitleEle?.isReady;
    if(!isReady) {
      return;
    }
    try {
      const noteDatas = this.libNoteService.getNoteData();
      if (this.recordService.isValidationError(noteDatas)) return;
      let current = noteDatas.find((noteData: any) => noteData.origin === this.origin);
      const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
      const staffList = this.store.selectSnapshot(DataSelectors.staffList(objectcd));
      const getStaffName = this.scheduleService.getStaffName(staffList);
      if (!this.isDecide) {
        this.endDate.patchValue({ date: moment(LIMIT_DATE.EDATE).format(this.dateFormat) });
      }
      this.record.ownunitcd = this.currentUnit;
      const formValue = this.scheduleService.getFormValue(this.form.value, getStaffName(this.record));
      const payload: NoteUpdate[] = [];
      const note = {
        ...this.record,
        ...formValue,
        v2contents: current.v2contents,
        contents: current.contents,
        dataV2Contents: undefined,
        noteblobs: current.noteblobs
      };
      // noted: ckapp not update odrstfcd
      // odrstfcd = null
      // backend handle null value
      note.odrstfcd = null;
      const operation = this.recordService.getOperation(this.record.noteid);
      const displayName = this.accesslog.getKidsDisplayName(this.record.ttlnm);
      payload.push({ operation, note, displayName });
      const _payload = await this.noteblobService.createNoteBlobPayload(payload);
      this.apiService.setNoteList(_payload)
        .pipe(
          filter((res) => !!res),
          takeUntil(this.onDestory$))
        .subscribe((res) => {
          const errorList = res
            .filter((item) => !!item.error)
            .map((item) => this.message.getErrorMessage(item.error!));

          if (errorList.length > 0) {
            this.dialogService.message(this.errorUpdateMsg);
          } else if (res.length > 0) {
            this.snackBar.open('保存しました');
          }

          if (errorList.length !== res.length) {
            this.canLeave = true;
            if (this.dai) {
              this.store.dispatch(new SelectUser(this.dai));
            }
            this.router.navigate(['', { outlets: { edit: null } }], { replaceUrl: true, queryParamsHandling: "preserve" }).finally(() => {
              if (operation === 'INSERT') {
                localStorage.removeItem(this.faceSheetCacheName);
              }
              this.store.dispatch(new TriggerHistory());
              history.go(-1);
            });
          }
        });
      this.webviewService.kidsWarningBeforeChangePageIndicator(false);
      this.cd.detectChanges();

    } catch (error) {
      // can use ckapi log
      console.log(error);
    }
  }
  // return to main facesheet screen
  protected close() {
    this.router.navigate(['..', { outlets: { edit: null }, relativeTo: this.route }], { replaceUrl: true, queryParamsHandling: "preserve" }).finally(() => {
      history.go(-1);
    });
  }
}
